"use client"
import { useState, useEffect } from 'react';
import { FaChevronRight, FaChevronLeft } from 'react-icons/fa';
import { RxTriangleRight } from "react-icons/rx";
import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { side_bar_list } from '@/utils/nav';
import { NextStepViewport } from 'nextstepjs';
import { checkIsAdmin } from '@/utils/all_apis/authApi';
import { getActiveProjectRole } from '@/utils/all_apis/project'

interface MenuItem {
  title: string;
  icon: any;
  subItems?: MenuItem[];
  link?: string;
  isDisabled?: string;
  hidden?: string[];
}

const menuItems: MenuItem[] = side_bar_list;

function useLocalStorage(key: any, defaultValue: any) {
  const [value, setValue] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = localStorage.getItem(key);
      return storedValue ? JSON.parse(storedValue) : defaultValue;
    }
    return defaultValue; // Fallback for server-side
  });

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(key, JSON.stringify(value));
    }
  }, [key, value]);

  return [value, setValue];
}

const Sidebar = ({ setCollapsed, collapsed }: any) => {
  const [openSubMenuIndex, setOpenSubMenuIndex] = useLocalStorage("openSubMenuIndex", null);
  const pathname = usePathname(); // This gives you just the pathname of the current page.
  const [isAdmin, setIsAdmin] = useState(false);
  const [projectRole, setProjectRole] = useState("");

  useEffect(() => {
    const fetchAdminStatus = async () => {
      const adminStatus = await checkIsAdmin();
      setIsAdmin(adminStatus);
    };

    const fetchProjectRole = async () => {
      const apiProjectRole = await getActiveProjectRole();
      if(apiProjectRole){
        setProjectRole(apiProjectRole.toLowerCase());
      }
    };

    fetchAdminStatus();
    fetchProjectRole();
  }, []);

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
    localStorage.setItem('isCollapsed', String(!collapsed));
  };

  const openCollapse = () => {
    setCollapsed(false);
    localStorage.setItem('isCollapsed', String(false));
  };

  const toggleSubMenu = (index: number) => {
    openCollapse();
    setOpenSubMenuIndex(openSubMenuIndex === index ? null : index);
    localStorage.setItem('openSubMenuIndex', String(openSubMenuIndex === index ? null : index));
  };

  const adminMenuItem: MenuItem = {
    title: 'Admin',
    icon: <FaChevronRight />,
    link: '/admin',
  };

  return (
    <>
      <div className={`hidden md:block fixed top-[48px] left-0 h-full bg-zoho-sidebar transition-all duration-300 pt-2 pl-2 pb-[200px] ${collapsed ? 'w-10' : 'w-[200px] overflow-y-scroll scrollbar-thin scrollbar-blue'}`}>
        <NextStepViewport id="scrollable-viewport">
          {menuItems.map((item, index) => (!item?.hidden || (item?.hidden && !item?.hidden.includes(projectRole))) && (
            <div key={"sidebar_" + index}>
              {item.subItems ? (
                <div className={`flex items-center py-2 ${collapsed ? 'pl-1' : 'pl-2'} cursor-pointer  ${item.subItems.map((item:any)=>item.link).includes(pathname) ? 'bg-zoho-sidebar-800 text-white' : 'hover:bg-zoho-sidebar-800'} rounded-l-lg`} onClick={() => toggleSubMenu(index)}>
                  <span className='text-zoho-sidebar-font font-normal font-sans text-sm'>{item.icon}</span>
                  <span className={`ml-2 ${collapsed ? 'hidden' : ''}`}><span className='text-zoho-sidebar-font font-normal font-sans text-sm'>{item.title}</span></span>
                  {!collapsed && (
                    <RxTriangleRight className={`text-zoho-sidebar-font font-normal font-sans text-lg ml-auto ${openSubMenuIndex === index ? 'rotate-90' : ''}`} />
                  )}
                </div>
              ) : (
                <Link href={item?.isDisabled == "yes"?'':(item.link || "#")} id={item.title == "Settings" ? "guidesettings" : undefined}>
                  <div className={`flex items-center py-2  ${collapsed ? 'pl-1' : 'pl-2'} ${pathname == item.link ? 'bg-zoho-green text-white' : 'hover:bg-zoho-sidebar-800 text-zoho-sidebar-font'} cursor-pointer rounded-l-lg`}>
                    <span className='font-normal font-sans text-sm'>{item.icon}</span>
                    <span className={`ml-2 ${collapsed ? 'hidden' : ''}`}><span className={`${item?.isDisabled == "yes"?"text-gray-500":""} font-normal font-sans text-sm`}>{item.title}</span></span>
                  </div>
                </Link>
              )}
              {item.subItems && openSubMenuIndex === index && (
                <div className={` ${collapsed ? 'ml-1' : 'ml-8'}`}>
                  {item.subItems.map((subItem, subIndex) => (!subItem?.hidden || (subItem?.hidden && !subItem?.hidden.includes(projectRole))) && (
                    <Link href={subItem?.isDisabled == "yes"?'':(subItem.link || "#")} key={"subindex_" + subIndex}>
                      <div className={`flex items-center p-2  rounded-l-lg ${pathname == subItem.link ? 'bg-zoho-green text-white' : 'text-gray-600 hover:bg-zoho-sidebar-800'}`}>
                        <span className='text-zoho-sidebar-font font-normal font-sans text-sm'>{subItem.icon}</span>
                        <span className={`ml-2 ${collapsed ? 'hidden' : ''}`}><span className={`${subItem?.isDisabled == "yes"?"text-gray-500":"text-zoho-sidebar-font"} font-normal font-sans text-sm`}>{subItem.title}</span></span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          {isAdmin && (<>
            <Link href={adminMenuItem.link || "#"}>
              <div className={`flex items-center py-2 ${collapsed ? 'pl-1' : 'pl-2'} ${pathname == adminMenuItem.link ? 'bg-zoho-green text-white' : 'hover:bg-zoho-sidebar-800 text-zoho-sidebar-font'} cursor-pointer rounded-l-lg`}>
                <span className='font-normal font-sans text-sm'>{adminMenuItem.icon}</span>
                <span className={`ml-2 ${collapsed ? 'hidden' : ''}`}><span className='font-normal font-sans text-sm'>{adminMenuItem.title}</span></span>
              </div>
            </Link>
            <Link href={"/photo"}>
              <div className={`flex items-center py-2 ${collapsed ? 'pl-1' : 'pl-2'} ${pathname == "/photo" ? 'bg-zoho-green text-white' : 'hover:bg-zoho-sidebar-800 text-zoho-sidebar-font'} cursor-pointer rounded-l-lg`}>
                <span className='font-normal font-sans text-sm'>{adminMenuItem.icon}</span>
                <span className={`ml-2 ${collapsed ? 'hidden' : ''}`}><span className='font-normal font-sans text-sm'>{"Photo"}</span></span>
              </div>
            </Link>
          </>
          )}
        </NextStepViewport>
      </div>
      <button onClick={toggleCollapse} className={`hidden md:flex bg-zoho-sidebar-800 justify-center items-center h-[35px] fixed bottom-0 p-2 text-zoho-sidebar-font font-normal font-sans text-sm transition-all duration-300  ${collapsed ? 'w-10' : 'w-[200px]'}`}>
        {collapsed ? <FaChevronRight className="h-4 w-4" /> : <FaChevronLeft className='h-4 w-4' />}
      </button>
    </>
  );
};

export default Sidebar;